import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { Star, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

import API from '../../config/api';

const DEFAULT_TESTIMONIALS = [
  {
    id: '1',
    quote: "Bought this for my mother before Dhanteras — she loves how elegant it looks and it arrived beautifully packed.",
    name: "Priya S.",
    location: "Bengaluru",
    rating: 5,
    avatar: "PS",
    verified: true
  },
  {
    id: '2',
    quote: "Quality feels premium and true to the pictures. The energisation note included was a nice touch.",
    name: "Rohit M.",
    location: "Pune",
    rating: 5,
    avatar: "RM",
    verified: true
  },
  {
    id: '3',
    quote: "Ordered as a gift for my wife's birthday. Fast delivery and she wears it daily now — very authentic experience.",
    name: "Ankit V.",
    location: "Delhi",
    rating: 5,
    avatar: "AV",
    verified: true
  },
  {
    id: '4',
    quote: "Second time ordering from Paramparik Puja. Consistent quality and the COD option is very convenient.",
    name: "Meera K.",
    location: "Hyderabad",
    rating: 5,
    avatar: "MK",
    verified: true
  }
];

const TestimonialsSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (err) {
        console.error('Error loading homepage testimonials:', err);
      }
    };
    fetchTestimonials();
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-12 bg-[#F3E7D0] border-t border-[#E7D9BC]">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#B85A15]">
            CUSTOMER LOVE
          </span>
          <h2 className="font-serif-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E1225] mt-1">
            What Our Customers Say
          </h2>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="w-10 h-px bg-[#B98B3E]" />
            <span className="w-2 h-2 bg-[#B98B3E] rotate-45" />
            <span className="w-10 h-px bg-[#B98B3E]" />
          </div>

          {/* Rating Summary */}
          <div className="flex items-center justify-center space-x-3 mt-6">
            <span className="font-serif-heading text-4xl font-bold text-[#4E1225]">4.9</span>
            <div className="flex flex-col items-start text-left">
              <div className="flex items-center text-[#D9711F]">
                {Array(5).fill(0).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <span className="text-xs text-[#83725B]">Based on 1,840+ verified devotees</span>
            </div>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex space-x-6">
            {testimonials.map((item, index) => {
              const initial = item.avatar || (item.name ? item.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'PS');
              return (
                <div
                  key={item.id || index}
                  className="flex-[0_0_90%] sm:flex-[0_0_48%] lg:flex-[0_0_31%] min-w-0"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="bg-[#FFFFFF] rounded-xl p-6 border border-[#E7D9BC] shadow-xs flex flex-col justify-between space-y-4 h-full"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-full bg-[#6E1B34] text-[#FBF4E7] font-bold text-xs flex items-center justify-center flex-shrink-0">
                          {initial}
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-[#2A1F16]">{item.name}</h4>
                          <p className="text-[11px] text-[#83725B]">{item.location || 'India'}</p>
                        </div>
                      </div>

                      <div className="flex text-[#D9711F]">
                        {Array(item.rating || 5).fill(0).map((_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 fill-current" />
                        ))}
                      </div>
                    </div>

                    <p className="text-xs sm:text-sm text-[#2A1F16] leading-relaxed italic">
                      "{item.quote}"
                    </p>

                    {item.verified !== false && (
                      <div className="flex items-center space-x-1.5 text-[11px] text-[#2E7D45] font-semibold pt-2 border-t border-[#E7D9BC]/50">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Verified Purchase</span>
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Arrow controls */}
        <div className="flex items-center justify-center space-x-3 mt-8">
          <button
            onClick={scrollPrev}
            className="w-10 h-10 rounded-full border border-[#E7D9BC] bg-[#FBF4E7] text-[#6E1B34] flex items-center justify-center hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollNext}
            className="w-10 h-10 rounded-full border border-[#E7D9BC] bg-[#FBF4E7] text-[#6E1B34] flex items-center justify-center hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
